import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Clock, CloudOff, Loader2, LogIn, LogOut } from 'lucide-react';
import { api } from '../../api/client';
import {
  WORK_LOCATIONS,
  WORK_LOCATION_LABELS,
  workLocationLabel,
  type WorkLocation,
} from '../../constants/work-locations';
import { formatElapsed, getGPS, useShiftElapsed } from '../../lib/shift-clock';
import { useAuth } from '../../contexts/AuthContext';
import { useMyWork } from '../../hooks/useMyWork';
import { CLOCK_LINKABLE_VERTICALS, clockLinkableItems, decodePickerValue, workItemLabel, workItemValue } from '../../lib/work-item-selection';
import { pickStr, pickerTitle, sortPickerJobs, CLOCK_IN_JOBS_KEY, NO_ASSIGNED_JOBS_TEXT } from '../../lib/job-picker';
import { jobStatusLabel } from '../../lib/job-status';
import { clockQueuedText, useClockTaps } from '../../lib/offline/use-clock-queue';

/**
 * ── THE CLOCK, ON THE HOME SCREEN ───────────────────────────────────────────
 *
 * The casing field tree has no bottom tab bar any more, and the Clock In tab
 * was the one entry on it that was not merely a shortcut: it was the only
 * always-visible surface telling a hand whether they were on the clock. Losing
 * that and replacing it with a tile would be a downgrade — a tile says where to
 * go, it does not say what is true.
 *
 * So this sits above the stat tiles and answers the question, and both
 * transitions happen here rather than one screen away.
 *
 * ── WHAT IS DELIBERATELY *NOT* HERE ─────────────────────────────────────────
 *
 * `/field/clock-in` still exists and is still linked from every state of this
 * card. It owns standby, switching the job on an open shift, the clock-out note
 * and photos, and the hand's own shifts for the period. This is the quick path,
 * not a replacement, and it must not grow into one.
 *
 * ── THE OPTIONAL JOB (operator decision, 2026-10-09) ───────────────────────
 *
 * Measured on TNT: of 59 field shifts, 38 were clocked in here with no job and
 * 21 through the full screen's picker — this card was the only door that could
 * not name a job, and it is the one most shifts use. So a FIELD shift may now
 * name one here: casing jobs (GET /jobs, the Clock In screen's own query) and
 * the hand's linkable work from GET /field/my-work (PT today). It is OPTIONAL
 * and defaults to "No job": with nothing picked the POST body is byte-identical
 * to what this card always sent, and nothing is ever guessed.
 *
 * ── WHY THERE IS A SHOP/FIELD CHOICE AND NO DEFAULT ─────────────────────────
 *
 * `work_location` is nullable and NULL is a real third state meaning "nobody
 * said" — see constants/work-locations.ts, which spells out why the picker on
 * ClockInScreen has no default either. A one-tap Clock In here would have to
 * either invent a location or write NULL onto a record that a person was
 * standing right in front of. Asking is two taps and is the honest version, so
 * the button stays disabled until the question is answered.
 *
 * ── THE POLL ────────────────────────────────────────────────────────────────
 *
 * Same query key, interval and staleness the removed FieldBottomNav used, so
 * the state stays live without a second source of truth. ClockInScreen shares
 * the key, which is what makes clocking in from there update this card on the
 * way back.
 */

type ShiftStatus = 'clocked_out' | 'active' | 'standby' | string;

/** The clock-in keys a pick adds: job_id for a casing job, the work-item pair otherwise, nothing for "No job". */
function jobBodyFields(value: string): { job_id?: string; work_item_vertical?: string; work_item_id?: string } {
  const sel = decodePickerValue(value);
  if (sel.kind === 'job') return { job_id: sel.jobId };
  if (sel.kind === 'workItem') return { work_item_vertical: sel.vertical, work_item_id: sel.id };
  return {};
}

/** "J-0072 — Vermilion Energy Inc. · Ensign 144 · Active" — the Clock In sheet's parts, on one line. */
function casingJobOptionLabel(job: any): string {
  const t = pickerTitle(job);
  const lead = t.rig !== null ? `${t.lead} · ${t.rig}` : t.lead;
  const number = pickStr(job, 'jobNumber', 'job_number') ?? '—';
  return `${number} — ${lead} · ${jobStatusLabel(job.status)}`;
}

export default function FieldClockAction() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [workLocation, setWorkLocation] = useState<WorkLocation | null>(null);
  const [error, setError] = useState<string | null>(null);
  // '' = "No job". Otherwise a casing job id, or a work item's prefixed value.
  const [jobPick, setJobPick] = useState('');

  const { hasVertical, user } = useAuth();
  const field = workLocation === 'field';
  // Fetched only once Field is chosen: a shop shift, and a hand who never
  // opens the choice, cost nothing. Same key and query as the Clock In
  // screen's picker, so the two share one cache entry: the hand's own crewed,
  // still-open jobs (api#1039), not every job in the company.
  const { data: jobs, isSuccess: jobsLoaded } = useQuery({
    queryKey: CLOCK_IN_JOBS_KEY,
    queryFn: () => api.timesheets.clockInJobs().then((r: any) => r.data?.data ?? r.data ?? []),
    enabled: field && hasVertical('casing_running'),
  });
  const { data: myWork } = useMyWork('open', { enabled: field && CLOCK_LINKABLE_VERTICALS.some((v) => hasVertical(v)) });
  const casingJobs: any[] = hasVertical('casing_running') ? sortPickerJobs(jobs ?? []) : [];
  const ptItems = clockLinkableItems(myWork?.items, hasVertical);
  const canPick = casingJobs.length + ptItems.length > 0;

  const { data: statusData, isLoading } = useQuery({
    queryKey: ['timesheet-status'],
    queryFn: () => api.timesheets.status().then((r) => {
      // `res.data?.data || res.data` reads the envelope as data when the API
      // answers {success: true, data: null} — every field then reads undefined
      // off a truthy object. Explicit unwrap instead.
      const body: any = r.data;
      return body && typeof body === 'object' && 'data' in body ? body.data : body;
    }),
    refetchInterval: 30000,
    staleTime: 10000,
  });

  // ── Clock taps go through the device queue (useClockTaps) ──
  // The same queue ClockInScreen uses: a tap is on the device before it is
  // sent, so a dead network or a closed tab cannot lose it, and a tap made on
  // one screen is the one the other shows. While taps are queued the last one
  // decides the state; the server's status takes over when the queue is empty.
  const {
    pending: pendingTaps, status: tapStatus, clockInAt, tapClockIn, tapClockOut,
  } = useClockTaps(user?.id, statusData);
  const status = tapStatus as ShiftStatus;
  const onTheClock = status === 'active' || status === 'standby';

  // ── Elapsed timer ──
  // Derived from `clock_in_at`, not from `elapsedMinutes`. The poll is every
  // 30s and this card is mounted and unmounted every time a hand taps through
  // to /field/clock-in and back; neither can move this number backwards now,
  // because there is no accumulator to lose. See useShiftElapsed.
  const elapsed = useShiftElapsed(onTheClock ? clockInAt : null);

  const clockInMut = useMutation({
    mutationFn: async () => {
      // The tap time is NOW, before the GPS wait, which can take seconds.
      const tappedAt = new Date().toISOString();
      const gps = await getGPS();
      setError(null);
      return tapClockIn({
        // Non-null by the time this runs — the button is disabled without it.
        ...(workLocation ? { work_location: workLocation } : {}),
        ...(gps ? { lat: gps.lat, lng: gps.lng } : {}),
        // No `pay_code_id`, and a job ONLY when the hand picked one for a
        // FIELD shift — never guessed, never carried over to a shop shift.
        // Nothing picked adds no key at all, so the body is what it always was.
        ...(field ? jobBodyFields(jobPick) : {}),
      }, tappedAt);
    },
    onSuccess: () => {
      setError(null);
      setWorkLocation(null);
      setJobPick('');
    },
    onError: (err: any) => setError(err?.response?.data?.error || err?.message || 'Clock in failed'),
    // Every outcome re-reads the shift, so the card shows what the server holds.
    onSettled: () => { qc.invalidateQueries({ queryKey: ['timesheet-status'] }); },
  });

  const clockOutMut = useMutation({
    mutationFn: () => {
      setError(null);
      // Closes the shift BY NAME, or chains to a clock-in still on the device.
      return tapClockOut({}, new Date().toISOString());
    },
    onSuccess: () => {
      setError(null);
    },
    onError: (err: any) => setError(err?.response?.data?.error || err?.message || 'Clock out failed'),
    onSettled: () => { qc.invalidateQueries({ queryKey: ['timesheet-status'] }); },
  });

  const busy = clockInMut.isPending || clockOutMut.isPending;

  // Nothing is claimed before the first reading lands. Rendering "Not clocked
  // in" while the answer is unknown is the same mistake as rendering 0 for an
  // absent number: it looks like data.
  if (isLoading) {
    return (
      <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4 mb-6 flex items-center gap-3">
        <Loader2 size={20} className="animate-spin text-fieldiq-accent" />
        <span className="text-sm text-fieldiq-text">Checking your shift…</span>
      </div>
    );
  }

  return (
    <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4 mb-6">
      {onTheClock ? (
        <>
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-green-400 animate-pulse" />
                <span className="text-sm font-semibold text-green-400">
                  {status === 'standby' ? 'On standby' : 'On the clock'}
                </span>
              </div>
              {/* Both spellings, because reads come back camelCase and writes
                  go out snake_case and this row is the write echoed back. An
                  absent or unrecognised value is an em dash — never "Field",
                  which is a location nobody chose. */}
              <p className="text-xs text-fieldiq-text mt-1">
                {workLocationLabel(
                  statusData?.timesheet?.work_location ?? statusData?.timesheet?.workLocation,
                )}
              </p>
            </div>
            <div className="text-2xl font-bold tabular-nums text-fieldiq-accent">
              {formatElapsed(elapsed)}
            </div>
          </div>

          <button
            onClick={() => clockOutMut.mutate()}
            disabled={busy}
            className="w-full min-h-[52px] rounded-xl bg-fieldiq-accent text-fieldiq-dark font-semibold flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98] transition-transform"
          >
            {clockOutMut.isPending
              ? <Loader2 size={20} className="animate-spin" />
              : <><LogOut size={20} /> Clock Out</>}
          </button>
        </>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-3">
            <Clock size={18} className="text-fieldiq-muted" />
            <span className="text-sm font-semibold">Not clocked in</span>
          </div>

          <p className="text-xs text-fieldiq-text mb-2">Where are you working?</p>
          <div className="grid grid-cols-2 gap-2 mb-3">
            {WORK_LOCATIONS.map((loc) => (
              <button
                key={loc}
                onClick={() => setWorkLocation(loc)}
                aria-pressed={workLocation === loc}
                className={`min-h-[44px] rounded-lg border-2 text-sm font-semibold transition-colors ${
                  workLocation === loc
                    ? 'border-fieldiq-accent text-fieldiq-accent bg-fieldiq-accent/10'
                    : 'border-fieldiq-border text-fieldiq-text'
                }`}
              >
                {WORK_LOCATION_LABELS[loc]}
              </button>
            ))}
          </div>

          {field && hasVertical('casing_running') && jobsLoaded && !canPick && (
            <p data-testid="no-assigned-jobs" className="text-xs text-fieldiq-text mb-3">{NO_ASSIGNED_JOBS_TEXT}</p>
          )}

          {field && canPick && (
            <label className="block mb-3">
              <span className="block text-xs text-fieldiq-text mb-1">Job (optional)</span>
              <select
                value={jobPick}
                onChange={(e) => setJobPick(e.target.value)}
                className="w-full min-h-[44px] rounded-lg border border-fieldiq-border bg-fieldiq-dark px-3 text-sm text-white"
              >
                <option value="">No job</option>
                {casingJobs.map((job: any) => (
                  <option key={job.id} value={job.id}>{casingJobOptionLabel(job)}</option>
                ))}
                {ptItems.map((item) => (
                  <option key={`wi-${item.id}`} value={workItemValue(item)}>{workItemLabel(item)}</option>
                ))}
              </select>
            </label>
          )}

          <button
            onClick={() => clockInMut.mutate()}
            disabled={!workLocation || busy}
            className="w-full min-h-[52px] rounded-xl bg-fieldiq-accent text-fieldiq-dark font-semibold flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98] transition-transform"
          >
            {clockInMut.isPending
              ? <Loader2 size={20} className="animate-spin" />
              : <><LogIn size={20} /> Clock In</>}
          </button>
        </>
      )}

      {pendingTaps.length > 0 && (
        <p role="status" className="text-xs text-amber-400 mt-2 flex items-center gap-1.5">
          <CloudOff size={12} />
          {clockQueuedText(pendingTaps.length)}
        </p>
      )}
      {error && (
        <p role="alert" className="text-xs text-red-400 mt-2">{error}</p>
      )}

      <button
        onClick={() => navigate('/field/clock-in')}
        className="w-full mt-3 flex items-center justify-center gap-1 text-xs text-fieldiq-text"
      >
        {onTheClock ? 'Standby, notes & this period' : 'Pick a job, or see this period'}
        <ChevronRight size={14} />
      </button>
    </div>
  );
}
